import api from '../../api/apiClient';
import type {
  AdminDashboard,
  AppNotification,
  Attachment,
  Comment,
  EmployeeWorkload,
  GroupWorkload,
  ManagerDashboard,
  Meta,
  MyPerformance,
  MyWeek,
  MyWork,
  OrganizationWorkload,
  Page,
  ProjectDrawings,
  Reassignment,
  TaskDetail,
  TaskSummary,
  TimeEntry,
  Timesheet,
  TimeEntryCategory,
} from './tasks.types';

/** Every task & workload endpoint answers `{ status, message?, data }`. */
async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

export interface DailyInput {
  workDate: string;
  hours: number;
}

export interface TaskListParams {
  search?: string;
  status?: string[];
  priority?: string;
  projectId?: number;
  employeeId?: number;
  departmentId?: number;
  dueFrom?: string;
  dueTo?: string;
  overdue?: boolean;
  scope?: 'mine' | 'team' | 'all';
  includeClosed?: boolean;
  topLevel?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

export interface TaskInput {
  title: string;
  description?: string;
  projectId: number;
  parentTaskId?: number | null;
  /** 0 clears it on update. */
  workActivityId?: number | null;
  /** 0 clears it on update. */
  typeOfWorkActivityId?: number | null;
  /** SHUTTERING / FRAMING; "" clears on update. */
  structuralWorkType?: string | null;
  /** e.g. ['C1','S3']; [] clears on update. */
  structuralDrawings?: string[] | null;
  priority?: string;
  startDate?: string | null;
  dueDate?: string | null;
  estimatedHours?: number | null;
  allocatedHours?: number | null;
  assigneeId?: number | null;
  requireAcceptance?: boolean;
  dailyAllocations?: DailyInput[];
  allowOverCapacity?: boolean;
}

export const tasksApi = {
  meta: () => data<Meta>(api.get('/api/tasks/meta')),
  projectDrawings: (projectId: number) => data<ProjectDrawings>(api.get(`/api/tasks/projects/${projectId}/drawings`)),
  list: (params: TaskListParams) =>
    data<Page<TaskSummary>>(
      api.get('/api/tasks', {
        params,
        // Spring reads a repeated `status=` as a list.
        paramsSerializer: { indexes: null },
      })
    ),
  get: (id: number) => data<TaskDetail>(api.get(`/api/tasks/${id}`)),
  create: (body: TaskInput) => data<TaskDetail>(api.post('/api/tasks', body)),
  update: (id: number, body: Partial<TaskInput> & { completionPercentage?: number }) =>
    data<TaskDetail>(api.put(`/api/tasks/${id}`, body)),
  remove: (id: number) => api.delete(`/api/tasks/${id}`),
  changeStatus: (id: number, status: string, reason?: string, completionPercentage?: number) =>
    data<TaskDetail>(api.patch(`/api/tasks/${id}/status`, { status, reason, completionPercentage })),

  assign: (
    id: number,
    body: {
      employeeId: number;
      allocatedHours?: number | null;
      startDate?: string | null;
      dueDate?: string | null;
      requireAcceptance?: boolean;
      dailyAllocations?: DailyInput[];
      allowOverCapacity?: boolean;
    }
  ) => data<TaskDetail>(api.post(`/api/tasks/${id}/assign`, body)),
  accept: (id: number) => data<TaskDetail>(api.post(`/api/tasks/${id}/accept`)),
  decline: (id: number, reason?: string) => api.post(`/api/tasks/${id}/decline`, { reason }),
  requestRelease: (id: number, reason: string, suggestedEmployeeId?: number | null) =>
    data<Reassignment>(api.post(`/api/tasks/${id}/release-request`, { reason, suggestedEmployeeId })),
  reassign: (
    id: number,
    body: {
      toEmployeeId: number;
      transferredHours?: number | null;
      reason: string;
      remarks?: string;
      requireAcceptance?: boolean;
      allowOverCapacity?: boolean;
    }
  ) => data<Reassignment>(api.post(`/api/tasks/${id}/reassign`, body)),
  timer: (id: number, action: 'start' | 'pause' | 'resume' | 'stop', notes?: string) =>
    data<TimeEntry>(api.post(`/api/tasks/${id}/timer/${action}`, notes ? { notes } : {})),
  plan: (assignmentId: number, days: DailyInput[], allowOverCapacity?: boolean) =>
    api.put(`/api/task-assignments/${assignmentId}/allocations`, { days, allowOverCapacity }),

  addComment: (id: number, comment: string) => data<Comment>(api.post(`/api/tasks/${id}/comments`, { comment })),
  editComment: (commentId: number, comment: string) =>
    data<Comment>(api.put(`/api/tasks/comments/${commentId}`, { comment })),
  deleteComment: (commentId: number) => api.delete(`/api/tasks/comments/${commentId}`),
  attach: (id: number, fileId: number) => data<Attachment>(api.post(`/api/tasks/${id}/attachments`, { fileId })),
  detach: (id: number, attachmentId: number) => api.delete(`/api/tasks/${id}/attachments/${attachmentId}`),
  addDependency: (id: number, relatedTaskId: number, type: string) =>
    api.post(`/api/tasks/${id}/dependencies`, { relatedTaskId, type }),
  removeDependency: (id: number, dependencyId: number) => api.delete(`/api/tasks/${id}/dependencies/${dependencyId}`),
};

export const reassignmentsApi = {
  list: (status?: string, page = 0, size = 50) =>
    data<Page<Reassignment>>(api.get('/api/task-reassignments', { params: { status, page, size } })),
  mine: () => data<{ offers: Reassignment[]; requests: Reassignment[] }>(api.get('/api/task-reassignments/mine')),
  approve: (
    id: number,
    body: {
      toEmployeeId?: number | null;
      transferredHours?: number | null;
      remarks?: string;
      requireAcceptance?: boolean;
      allowOverCapacity?: boolean;
    }
  ) => data<Reassignment>(api.post(`/api/task-reassignments/${id}/approve`, body)),
  reject: (id: number, remarks?: string) => data<Reassignment>(api.post(`/api/task-reassignments/${id}/reject`, { remarks })),
  cancel: (id: number, remarks?: string) => data<Reassignment>(api.post(`/api/task-reassignments/${id}/cancel`, { remarks })),
  accept: (id: number) => data<Reassignment>(api.post(`/api/task-reassignments/${id}/accept`)),
  decline: (id: number, reason?: string) =>
    data<Reassignment>(api.post(`/api/task-reassignments/${id}/decline`, { reason })),
};

export const myWorkApi = {
  day: (date?: string) => data<MyWork>(api.get('/api/my-work', { params: { date } })),
  week: (date?: string) => data<MyWeek>(api.get('/api/my-work/week', { params: { date } })),
  performance: (weeks = 8) => data<MyPerformance>(api.get('/api/my-work/performance', { params: { weeks } })),
};

export const timesheetApi = {
  weekly: (date?: string, employeeId?: number) =>
    data<Timesheet>(api.get('/api/timesheets/weekly', { params: { date, employeeId } })),
  submit: (weekStart: string) => data<Timesheet>(api.post('/api/timesheets/weekly/submit', { weekStart })),
  review: () => data<Timesheet[]>(api.get('/api/timesheets/review')),
  approve: (id: number, remarks?: string) => data<Timesheet>(api.post(`/api/timesheets/${id}/approve`, { remarks })),
  reject: (id: number, remarks: string) => data<Timesheet>(api.post(`/api/timesheets/${id}/reject`, { remarks })),
  /** Either `taskId`, or `categoryId` for time outside any task. */
  log: (body: {
    taskId?: number;
    categoryId?: number;
    workDate: string;
    startTime?: string;
    endTime?: string;
    hours?: number;
    notes?: string;
  }) => data<TimeEntry>(api.post('/api/time-entries', body)),
  removeEntry: (id: number) => api.delete(`/api/time-entries/${id}`),
};

/** Categories for time outside a task; the admin maintains them on Reference Data. */
export const timeCategoriesApi = {
  list: () => data<TimeEntryCategory[]>(api.get('/api/time-entry-categories')),
};

export const workloadApi = {
  employee: (id: number, from?: string, to?: string) =>
    data<EmployeeWorkload>(api.get(`/api/workload/employees/${id}`, { params: { from, to } })),
  team: (from?: string, to?: string, departmentId?: number) =>
    data<GroupWorkload>(api.get('/api/workload/team', { params: { from, to, departmentId } })),
  departments: (from?: string, to?: string) =>
    data<GroupWorkload[]>(api.get('/api/workload/departments', { params: { from, to } })),
  organization: (from?: string, to?: string) =>
    data<OrganizationWorkload>(api.get('/api/workload/organization', { params: { from, to } })),
  manager: (date?: string, departmentId?: number) =>
    data<ManagerDashboard>(api.get('/api/workload/dashboard/manager', { params: { date, departmentId } })),
  admin: (from?: string, to?: string) =>
    data<AdminDashboard>(api.get('/api/workload/dashboard/admin', { params: { from, to } })),
};

export const notificationsApi = {
  list: (unreadOnly = false, page = 0, size = 15) =>
    data<Page<AppNotification>>(api.get('/api/notifications', { params: { unreadOnly, page, size } })),
  unreadCount: () => data<{ count: number }>(api.get('/api/notifications/unread-count')),
  read: (id: number) => api.post(`/api/notifications/${id}/read`),
  readAll: () => api.post('/api/notifications/read-all'),
};
